import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Company, Job, FirmWorkflow } from '../types/index'
import { SfLog } from '@app/adapters/logger'
import api from '@app/insights/api'
import { useToastStore } from '@app/shared/pinia/toast'
import useI18n from '@app/shared/vue/composables/use-I18n'
import {
  ARCHIVE_WORKFLOW_ACTION,
  LOCK_PERIOD_ACTION,
  CREATE_REMARK_ON_COMPANY_ACTION,
  CREATE_REMARK_ON_RECONCILIATION_TEXT_ACTION,
} from '../components/actions/constants'
import { getCurrentUserId } from '@app/adapters/silverfin'

const toastKeyPerAction = {
  [ARCHIVE_WORKFLOW_ACTION]: 'archive_workflows',
  [LOCK_PERIOD_ACTION]: 'lock_period',
  [CREATE_REMARK_ON_COMPANY_ACTION]: 'create_remark',
  [CREATE_REMARK_ON_RECONCILIATION_TEXT_ACTION]: 'create_remark',
} as Record<string, string>

export const useBulkActionsStore = defineStore('bulkActions', () => {
  const toastStore = useToastStore()
  const I18n = useI18n()

  const jobs = ref<Job[]>([])
  const nonActionableCompanies = ref<Company[]>([])
  const selectedWorkflow = ref<FirmWorkflow | null>(null)

  const currentUserId = parseInt(getCurrentUserId() || '0', 10)

  const isActionRunning = computed(() =>
    jobs.value.some((job) => job.running && !job.finished)
  )

  const actionsInProgress = computed(() =>
    jobs.value.filter((job) => job.running && !job.finished)
  )

  const actionsInProgressByCurrentUser = computed(() => {
    return actionsInProgress.value.filter(
      (job) => job.user_id === currentUserId
    )
  })

  function raiseToast(status: Job): void {
    if (status.user_id !== currentUserId) {
      return
    }

    if (!status.action_name) {
      return
    }

    const i18nKey = toastKeyPerAction[status.action_name]
    if (!i18nKey) {
      return
    }

    const message = I18n.t(`companies_overview.actions.${i18nKey}.toast`, {
      count: status.total_count,
    })

    toastStore.raiseToast({
      id: status.insight_action_run_id.toString(),
      title: I18n.t('companies_overview.actions.toast_title'),
      message,
      variant: 'success',
      size: 'md',
      actions: [],
    })
  }

  function setActionStatus(status: Job): void {
    const index = jobs.value.findIndex(
      (job) => job.insight_action_run_id === status.insight_action_run_id
    )

    if (index !== -1) {
      const previousStatus = jobs.value[index]

      jobs.value.splice(index, 1, { ...previousStatus, ...status })

      if (status.finished && !previousStatus.finished) {
        raiseToast(previousStatus)
      }
    } else {
      jobs.value.push(status)
    }
  }

  async function fetchNonActionableCompanies(
    actionName: string,
    companyIds: [],
    payload: Record<string, unknown>
  ): Promise<void> {
    const data: Company[] = (await api.fetchNonActionableCompanies({
      actionName,
      companyIds,
      payload,
    })) as Company[]
    if (data) {
      nonActionableCompanies.value = data
    } else {
      SfLog.info('Failed to fetch non-actionable companies', {
        extra: {
          actionName,
          payload,
        },
      })
    }
  }

  function setSelectedWorkflow(fwf: FirmWorkflow): void {
    selectedWorkflow.value = fwf
  }

  return {
    jobs,
    isActionRunning,
    actionsInProgress,
    actionsInProgressByCurrentUser,
    setActionStatus,
    nonActionableCompanies,
    fetchNonActionableCompanies,
    selectedWorkflow,
    setSelectedWorkflow,
  }
})
